import { useCallback, useEffect, useRef, useState } from "react";
import type { MyToolsError, MyToolsResponse } from "@carbon-os/server/contract";
import { setPreviewMode } from "./preview";
import { sessionFetch } from "./session";
import { beginSignIn, clearSignInAttempt, isRedirecting, shouldRedirect } from "./signin";

export type MyToolsState =
  | { kind: "loading" }
  | { kind: "ready"; data: MyToolsResponse }
  | { kind: "signin_failed" }
  | { kind: "preview_failed"; message: string }
  | { kind: "wrong_account"; signedInAs: string | null; signOutUrl: string }
  /** Verified, but Gatekeeper does not know them: every page shows only this. */
  | { kind: "not_in_gatekeeper"; signedInAs: string | null; signOutUrl: string; accessRequestUrl: string | null }
  /** `failures` counts this visit's failed attempts; `retrying` keeps the panel up during Try again. */
  | { kind: "unavailable"; failures: number; retrying: boolean }
  | { kind: "not_connected"; message: string };

type Outcome = Exclude<MyToolsState, { kind: "unavailable" }> | { kind: "unavailable" };

export interface LoadDeps {
  fetch: typeof fetch;
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  navigate: (url: string) => void;
  returnTo: string;
  now: number;
}

/**
 * The page's one data request. Only a 200 can produce an empty list; every
 * other answer, and a request that never arrives, is an error state.
 */
export async function loadMyTools(deps: LoadDeps): Promise<Outcome> {
  let res: Response;
  try {
    res = await deps.fetch("/api/my-tools", {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
  } catch {
    return { kind: "unavailable" };
  }

  if (res.status === 200) {
    const data = (await res.json().catch(() => null)) as MyToolsResponse | null;
    if (!data || !Array.isArray(data.tools)) return { kind: "unavailable" };
    setPreviewMode(Boolean(data.viewingAs));
    clearSignInAttempt(deps.storage);
    return { kind: "ready", data };
  }

  const body = (await res.json().catch(() => ({}))) as Partial<MyToolsError>;

  if (res.headers.get("X-CarbonOS-Preview") === "1") {
    setPreviewMode(true);
    return { kind: "preview_failed", message: body.error ?? "This preview could not be loaded. Return to your account to continue." };
  }
  if (res.status === 401) {
    if (!shouldRedirect(deps.now, deps.storage)) return { kind: "signin_failed" };
    beginSignIn(deps.returnTo, deps.storage, deps.navigate, deps.now);
    // The page is navigating away; keep the skeleton up until it does.
    return { kind: "loading" };
  }
  if (res.status === 403 && body.code === "not_in_gatekeeper") {
    return {
      kind: "not_in_gatekeeper",
      signedInAs: body.signedInAs ?? null,
      signOutUrl: body.signOutUrl ?? "/auth/signout",
      accessRequestUrl: body.accessRequestUrl ?? null,
    };
  }
  if (res.status === 403) {
    return {
      kind: "wrong_account",
      signedInAs: body.signedInAs ?? null,
      signOutUrl: body.signOutUrl ?? "/auth/signout",
    };
  }
  if (res.status === 503 && body.code === "not_connected") {
    return { kind: "not_connected", message: body.error ?? "" };
  }
  return { kind: "unavailable" };
}

export function browserDeps(): LoadDeps {
  return {
    fetch: (...args) => sessionFetch(...args),
    storage: window.sessionStorage,
    navigate: (url) => window.location.assign(url),
    returnTo: window.location.href,
    now: Date.now(),
  };
}

export function useMyTools(): { state: MyToolsState; retry: () => void } {
  const [state, setState] = useState<MyToolsState>({ kind: "loading" });
  const failures = useRef(0);
  const inFlight = useRef(false);
  // Refs survive StrictMode's mount-unmount-mount, so the page still makes one request.
  const started = useRef(false);

  const load = useCallback(async () => {
    inFlight.current = true;
    const outcome = await loadMyTools(browserDeps());
    inFlight.current = false;
    if (outcome.kind === "unavailable") {
      failures.current += 1;
      setState({ kind: "unavailable", failures: failures.current, retrying: false });
    } else if (outcome.kind === "signin_failed" && isRedirecting()) {
      // The training request's 401 has already sent the page to sign in, which
      // is what wrote the marker; keep the skeleton up until it leaves.
      setState({ kind: "loading" });
    } else {
      setState(outcome);
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  const retry = useCallback(() => {
    if (inFlight.current) return;
    setState((current) => (current.kind === "unavailable" ? { ...current, retrying: true } : current));
    void load();
  }, [load]);

  return { state, retry };
}
